import { create } from 'zustand';
import type { AlertPreviewCopy } from './alert-copy';

/**
 * Persistent, in-app attention alerts for internal team chat.
 *
 * Realtime can deliver the same message twice (for example `message.new` and
 * the directed `message.mention` event). `seenEventIds` deduplicates those
 * echoes for the lifetime of the authenticated session. Multiple NEW messages
 * in the same channel are grouped into one card so an unattended support
 * channel cannot cover the whole screen, but the card itself never expires:
 * only an explicit acknowledgement/open action removes it.
 */

export interface TeamChatLiveAlertInput {
  eventId: string;
  channelId: string;
  channelName: string;
  isDirect: boolean;
  senderName: string;
  preview: string;
  /** Product fallback only; never infer this from a customer's prose. */
  previewCopy?: AlertPreviewCopy;
  createdAt: string;
  isMention?: boolean;
}

export interface TeamChatLiveAlert extends TeamChatLiveAlertInput {
  /** Number of distinct messages grouped into this channel card. */
  messageCount: number;
}

export interface TeamChatLiveAlertMerge {
  alerts: TeamChatLiveAlert[];
  isNewEvent: boolean;
}

/**
 * Pure grouping/dedup planner. The most recently updated channel stays first.
 * `seenEventIds` includes dismissed messages, preventing a reconnect echo from
 * resurrecting an alert the user already confirmed.
 */
export function mergeTeamChatLiveAlert(
  alerts: TeamChatLiveAlert[],
  seenEventIds: string[],
  input: TeamChatLiveAlertInput,
): TeamChatLiveAlertMerge {
  if (seenEventIds.includes(input.eventId)) {
    return { alerts, isNewEvent: false };
  }

  const current = alerts.find((alert) => alert.channelId === input.channelId);
  const next: TeamChatLiveAlert = current
    ? {
        ...current,
        ...input,
        // A later literal message must replace an earlier product fallback.
        previewCopy: input.previewCopy,
        isMention: current.isMention || input.isMention,
        messageCount: current.messageCount + 1,
      }
    : { ...input, messageCount: 1 };

  return {
    alerts: [next, ...alerts.filter((alert) => alert.channelId !== input.channelId)],
    isNewEvent: true,
  };
}

const MAX_SEEN_EVENTS = 250;

interface TeamChatLiveAlertState {
  alerts: TeamChatLiveAlert[];
  seenEventIds: string[];
  /**
   * Server-backed unread previews dismissed without marking the channel read.
   * A concrete value hides only that channel snapshot; `*` hides it until the
   * next realtime message clears the channel entry.
   */
  dismissedUnreadVersions: Record<string, string>;
  /** Channel rendered in the full `/chat` page, if that page is mounted. */
  activeFullViewChannelId: string | null;
  /** Returns false for a realtime echo that was already processed. */
  enqueue: (input: TeamChatLiveAlertInput) => boolean;
  acknowledge: (channelId: string) => void;
  dismissUnread: (channelId: string, version?: string) => void;
  setActiveFullViewChannel: (channelId: string | null) => void;
  clear: () => void;
}

export const useTeamChatLiveAlertStore = create<TeamChatLiveAlertState>((set, get) => ({
  alerts: [],
  seenEventIds: [],
  dismissedUnreadVersions: {},
  activeFullViewChannelId: null,
  enqueue: (input) => {
    const state = get();
    const merged = mergeTeamChatLiveAlert(state.alerts, state.seenEventIds, input);
    if (!merged.isNewEvent) return false;
    const dismissedUnreadVersions = { ...state.dismissedUnreadVersions };
    delete dismissedUnreadVersions[input.channelId];
    set({
      alerts: merged.alerts,
      seenEventIds: [input.eventId, ...state.seenEventIds].slice(0, MAX_SEEN_EVENTS),
      dismissedUnreadVersions,
    });
    return true;
  },
  acknowledge: (channelId) =>
    set((state) => ({
      alerts: state.alerts.filter((alert) => alert.channelId !== channelId),
    })),
  dismissUnread: (channelId, version = '*') =>
    set((state) => ({
      dismissedUnreadVersions: {
        ...state.dismissedUnreadVersions,
        [channelId]: version,
      },
    })),
  setActiveFullViewChannel: (channelId) => set({ activeFullViewChannelId: channelId }),
  clear: () =>
    set({
      alerts: [],
      seenEventIds: [],
      dismissedUnreadVersions: {},
      activeFullViewChannelId: null,
    }),
}));
